export const windowReload = () => {
  if (window) {
    window.location.reload();
  }
};

export const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const returnDelayInSeconds = (delay: string): number => {
  // Predefined delays
  const predefinedDelays: Record<string, number> = {
    '1 hour': 60 * 60,
    '3 hours': 60 * 60 * 3,
    '8 hours': 60 * 60 * 8,
    '1 day': 60 * 60 * 24,
    '3 days': 60 * 60 * 24 * 3,
    '7 days': 60 * 60 * 24 * 7,
    '14 days': 60 * 60 * 24 * 14,
    '1 month': 60 * 60 * 24 * 30,
  };

  if (predefinedDelays[delay]) {
    return predefinedDelays[delay];
  }

  // Handle custom delays in days
  const customDelayMatch = delay.match(/^(\d+)\s*days?$/);
  if (customDelayMatch) {
    const days = parseInt(customDelayMatch[1], 10);
    return 60 * 60 * 24 * days;
  }

  // Default to 1 hour if no valid match
  return 60 * 60 * 1;
};

const allowedSuffix = ['k', 'K', 'm', 'M'];

export const parseDiscoveryFilterAmount = (amount: number | string): number => {
  let internalVal: number | string = `${amount}`;
  let multiplier = 1;
  if (allowedSuffix.includes(internalVal[internalVal.length - 1])) {
    const suffix = internalVal[internalVal.length - 1];
    internalVal = internalVal.slice(0, internalVal.length - 1);
    if (suffix === 'k' || suffix === 'K') {
      multiplier = 1000;
    } else if (suffix === 'm' || suffix === 'M') {
      multiplier = 1000000;
    }
  }

  internalVal = parseFloat(internalVal);

  if (Number.isNaN(internalVal)) return 0;

  return internalVal * multiplier;
};

export const roundToNearestMultipleForDiscoveryFilter = (amount: number, type: 'min' | 'max'): number => {
  const internalVal = parseDiscoveryFilterAmount(amount);

  if (internalVal <= 0) return 0;
  if (internalVal < 1000) return 1000;
  if (internalVal < 5000) return internalVal;
  if (type === 'min') return Math.floor(internalVal / 5000) * 5000;
  if (type === 'max') return Math.ceil(internalVal / 5000) * 5000;
  return 0;
};

export const getPrimaryOrConnectedEmail = (connectedEmailList: any) => {
  const primaryEmail = connectedEmailList?.find(
    (email: any) => email?.isPrimary === true && email?.potentialDisconnect === false
  );
  if (primaryEmail && primaryEmail?.isSmtpSuccess) {
    return primaryEmail?.email;
  } else {
    return (
      connectedEmailList?.find((email: any) => email?.isSmtpSuccess === true && email?.potentialDisconnect === false)
        ?.email || connectedEmailList?.[0]?.email
    );
  }
};

export const getError = (response: any) => {
  if (response?.status) {
    return null;
  }
  if (typeof response?.data === 'string') {
    return response?.data;
  }
  if (response?.data?.message) {
    return response?.data?.message;
  }
  if (response?.message) {
    return response?.message;
  }
  return 'Something went wrong';
};

/**
 * Converts a base64 string to a Blob object
 * @param base64String - The base64 string to convert (can include data URI scheme)
 * @param contentType - The MIME type of the data
 * @returns Blob object created from the base64 string
 */
export const base64ToBlob = (base64String: string, contentType: string = ''): Blob => {
  // If the base64 string includes a data URI scheme, extract just the base64 part
  const base64Data = base64String.includes(',') ? base64String.split(',')[1] : base64String;

  // Decode the base64 string to binary
  const binaryString = window.atob(base64Data);

  // Create a Uint8Array from the binary string
  const byteArray = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    byteArray[i] = binaryString.charCodeAt(i);
  }

  // Create and return a Blob from the byte array
  return new Blob([byteArray], { type: contentType });
};
